import {
  PatchWithMeta,
  PagedResponse,
  QueryParams,
  ValidatedResponse,
  Model,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { EventNotification } from "../domain/EventNotification";
import { eventNotificationsService, resourceTagsService, shareService, usersService } from "../services";
import {
  ActionPayload,
  ASSIGNING_ITEM,
  CLOSING_ITEM,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  loadedItem,
  loadedItems,
  loadedShare,
  loadingItem,
  LOADING_ITEM,
  LOADING_ITEMS,
  SENDING_ATTACHMENTS,
  SENDING_MESSAGE,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
  UPDATING_SHARE,
} from "./actions";
import { authSelector, feedSelector } from "./selectors";
import { History } from "history";
import { COMMS_MODULE_NAME, MODULE_NAME } from "../moduleName";
import { ShareRecord } from "../domain/ShareRecord";
import {
  refreshActivityFeed,
  waitActivityFeed,
} from "@ofqual/portal-activity-feed";
import { UserType } from "./types";

function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}

function* watchCreateItemSaga() {
  yield takeEvery(CREATING_ITEM, createItemSaga);
}

function* watchCloseItemSaga() {
  yield takeEvery(CLOSING_ITEM, closeItemSaga);
}

function* watchAssignItemSaga() {
  yield takeEvery(ASSIGNING_ITEM, assignItemSaga);
}

function* watchSendMessageSaga() {
  yield takeEvery(SENDING_MESSAGE, sendMessageSaga);
}

function* watchAttachmentsSaga() {
  yield takeLatest(SENDING_ATTACHMENTS, attachmentsSaga);
}

function* watchUpdateShareSaga() {
  yield takeLatest(UPDATING_SHARE, updateShareSaga);
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<EventNotification> = yield call(
    [eventNotificationsService, eventNotificationsService.search],
    { ...payload },
    { token: auth.token }
  );
  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
  const { token, userType } = yield select(authSelector);
  
  const tagsCall = () =>
  userType === UserType.Ofqual
    ? resourceTagsService.get(
      payload,
      {
        token,
      }
    )
    : Promise.resolve();

  const { item, share, resourceTags } = yield all({
    item: call(
      [eventNotificationsService, eventNotificationsService.get],
      payload,
      {
        token,
      }
    ),
    share: call([shareService, shareService.get], payload, {
      token,
    }),
    resourceTags: call(tagsCall),
  });
  item.resourceTags = resourceTags;

  const u: Model = yield call(
    [usersService, usersService.getByUpn], 
    item.createdByUpn,
    { token }
  );

  item.creatorEmail = u.alternateEmail || u.upn;

  yield put(loadedItem(item, share));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const { token, userType } = yield select(authSelector);
  const { id, patchWithMeta, resourceTagsIds } = payload as {
    id: string;
    patchWithMeta: PatchWithMeta<EventNotification>;
    resourceTagsIds: number[];
  };

    // netters since we only read the tags when ofqual I guess we only want to write them when ofqual  
    const tagsCall = () =>
    (userType === UserType.Ofqual && resourceTagsIds !== undefined)
    ? resourceTagsService.post(
      id,
      resourceTagsIds,
      {
        token,
      }
    )
    : Promise.resolve();

    const { validatedResponse } = yield all({
    validatedResponse: call(
      [eventNotificationsService, eventNotificationsService.update],
      id,
      patchWithMeta,
      {
        token,
      }
    ),
    tags: call(tagsCall),
    
    });

  if (validatedResponse.isValid) {
    const feedState: ReturnType<typeof feedSelector> = yield select(feedSelector);
    const params = feedState?.[id]?.params;
    yield put(refreshActivityFeed(id, params));
    yield put(loadingItem(id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* updateShareSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id, share } = payload as {
    id: string;
    share: ShareRecord;
  };

  const shareResponse: ShareRecord = yield call(
    [shareService, shareService.update],
    id,
    share,
    { token }
  );

  const feedState: ReturnType<typeof feedSelector> = yield select(feedSelector);
  const params = feedState?.[id]?.params;
  yield put(refreshActivityFeed(id, params));
  yield put(loadedShare(shareResponse));
}

function* createItemSaga({ payload }: ActionPayload<EventNotification>) {
  const { token } = yield select(authSelector);

  const validatedResponse: ValidatedResponse<EventNotification> = yield call(
    [eventNotificationsService, eventNotificationsService.create],
    payload,
    {
      token,
    }
  );

  if (validatedResponse.isValid) {
    yield put({ type: CREATED_ITEM });
    yield call(() =>
      navigate(`/${COMMS_MODULE_NAME}/${MODULE_NAME}/${validatedResponse.data.id}`)
    );
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* closeItemSaga({
  payload,
}: ActionPayload<{ id: string; isClose: boolean; reason: string }>) {
  const { token } = yield select(authSelector);
  const { id, isClose, reason } = payload;

  const eventNotification: EventNotification = yield call(
    [
      eventNotificationsService,
      isClose
        ? eventNotificationsService.close
        : eventNotificationsService.reopen,
    ],
    id,
    reason,
    {
      token,
    }
  );
  const feedState: ReturnType<typeof feedSelector> = yield select(feedSelector);
  const params = feedState?.[id]?.params;
  yield put(refreshActivityFeed(id, params));
  yield put(loadedItem(eventNotification));
}

function* assignItemSaga({
  payload,
}: ActionPayload<{
  id: string;
  assignedToUpn: string;
  assignedToDisplayName: string;
}>) {
  const { token, userType } = yield select(authSelector);
  const { id, assignedToUpn, assignedToDisplayName } = payload;

  // netters I think we need to read in the resourcetags as well
  const tagsCall = () =>
  userType === UserType.Ofqual
    ? resourceTagsService.get(
      id,
      {
        token,
      }
    )
    : Promise.resolve();

  const { eventNotification ,resourceTags } = yield all({
    eventNotification: call(
    [eventNotificationsService, eventNotificationsService.assign],
    id,
    assignedToUpn,
    assignedToDisplayName,
    {
      token,
    }
  ),
  resourceTags: call(tagsCall),
  });

  eventNotification.resourceTags = resourceTags;
  yield put(loadedItem(eventNotification));

}

function* sendMessageSaga({
  payload,
}: ActionPayload<{ id: string; message: string}>) {
  const { token , userType} = yield select(authSelector);
  const { id, message } = payload;

  const tagsCall = () =>
  userType === UserType.Ofqual
    ? resourceTagsService.get(
    id,
    {
      token,
    }
  )
  : Promise.resolve();

  const { eventNotification ,resourceTags } = yield all({
    eventNotification: call(
    [eventNotificationsService, eventNotificationsService.sendMessage],
    id,
    message,
    {
      token,
    }
  ),
  resourceTags: call(tagsCall),
  });

  eventNotification.resourceTags = resourceTags;
  const feedState: ReturnType<typeof feedSelector> = yield select(feedSelector);
  const params = feedState?.[id]?.params;
  yield put(refreshActivityFeed(id, params));
  yield put(loadedItem(eventNotification));
}

function* attachmentsSaga({
  payload,
}: ActionPayload<{ id: string; files: File[]; note: string }>) {
  const { token, userType } = yield select(authSelector);
  const { id, files, note } = payload;

  const tagsCall = () =>
    userType === UserType.Ofqual
      ? resourceTagsService.get(
        id,
        {
          token,
        }
      )
      : Promise.resolve();

  yield put(waitActivityFeed(id));

  const { eventNotification ,resourceTags } = yield all({
    eventNotification: call(
    [eventNotificationsService, eventNotificationsService.attachments],
    id,
    files,
    note,
    {
      token,
    }
  ),
  resourceTags: call(tagsCall),
  });

  eventNotification.resourceTags = resourceTags;
  const feedState: ReturnType<typeof feedSelector> = yield select(feedSelector);
  const params = feedState?.[id]?.params;
  yield put(refreshActivityFeed(id, params));
  yield put(loadedItem(eventNotification));
}
export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchCreateItemSaga(),
    watchCloseItemSaga(),
    watchAssignItemSaga(),
    watchSendMessageSaga(),
    watchAttachmentsSaga(),
    watchUpdateShareSaga(),
  ]);
}
